// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TerceraChartTradingOperation } from '@front/chart/Utils/ChartConstants';
import { DataCache } from '@shared/commons/DataCache';
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { GenerateReportMessages } from '@shared/commons/GenerateReportMessages';
import { PositionEdit } from '@shared/commons/cache/OrderParams/PositionEdit';
import { SLTPTriggerUtils } from '@shared/commons/cache/OrderParams/SLTPTriggerUtils';
import { Resources } from '@shared/localizations/Resources';
import { PanelNames } from '@front/controls/UtilsClasses/FactoryConstants';
import { SlTpPriceType } from '../Enums/Constants';
import { OperationType } from './OperationType';
import { OrderEditUpdateData } from './OrderEditUpdateData';
import { TIF } from './OrderTif';
import { OrderType } from './OrderType';
import { SlTpUtils } from './SlTpUtils';
import { PlacedFrom } from './PlacedFrom';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { SlTpHolder } from './SlTpHolder';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { BaseSettingsUtils } from '@shared/commons/UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { SLTPMode } from '@shared/commons/cache/OrderParams/SLTPEdit';
import { Position } from '@shared/commons/cache/Position';
export class ChartTradingCore {
    constructor(isMobileChart = false) {
        this.isMobileChart = false;
        this.myTradeNumeric = null;
        this.myVisualWidget = null;
        this.chartOE = null;
        this.terceraChartRactive = null;
        this.TradingNumericErrorChecker = null;
        this.EditPositionScreen = null;
        this.ModifyOrderScreen = null;
        this.TerceraMessageBox = null;
        this.MainWindowManager = null;
        this.getChartSLTPHolder = function (orderParams) {
            // Chart SL/TP are always offset.
            // const offsetType = BaseSettings.ShowOffsetIn;
            const instrument = orderParams.instrument;
            const sltpHolder = new SlTpHolder();
            const slEnabled = !isNaN(orderParams.slPriceValue);
            // let trStopEnabled = this.get('enabledSL') && this.get('trStopChecked')
            if (slEnabled) {
                sltpHolder.StopLossPriceValue = orderParams.slPriceValue;
                // OrderUtils.toRawTicks(orderParams.slPriceValue, offsetType, instrument)
                // sltpHolder.StopLossPriceType = trStopEnabled
                //     ? SlTpPriceType.TrOffset
                //     : SlTpPriceType.Offset
                sltpHolder.StopLossPriceType = SlTpPriceType.Absolute;
                // if (this.get('isSLLVisible'))
                //     sltpHolder.StopLossLimitPriceValue =
                //         OrderUtils.toRawTicks(this.get('priceSLL'), offsetType, instrument)
            }
            const tpEnabled = !isNaN(orderParams.tpPriceValue);
            if (tpEnabled) {
                sltpHolder.TakeProfitPriceValue = orderParams.tpPriceValue;
                // OrderUtils.toRawTicks(orderParams.tpPriceValue, offsetType, instrument)
                sltpHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
            }
            const anyEnabled = slEnabled /* || trStopEnabled */ || tpEnabled;
            if (anyEnabled) {
                // #110841
                sltpHolder.SLTPTriggerShortValue = SLTPTriggerUtils.GetTriggersShortForInstrument(instrument);
            }
            return sltpHolder;
        };
        this.isMobileChart = isMobileChart;
    }
    ChartVisualTrading(order, newData, instrumentItem) {
        var _a, _b, _c;
        const confirm = !this.isMobileChart;
        switch (newData.action) {
            case TerceraChartTradingOperation.ActionButton: {
                const tc = this.terceraChartRactive.terceraChart;
                const lastMouseMoveArgs = tc.lastMouseMoveArgs;
                const menuItems = tc.GetContextMenuItems(lastMouseMoveArgs, tc);
                if (isValidArray(menuItems) && !this.isMobileChart) {
                    showContextMenu({
                        x: lastMouseMoveArgs.ClientLocation.X,
                        y: lastMouseMoveArgs.ClientLocation.Y,
                        items: menuItems
                    });
                }
                return;
            }
            case TerceraChartTradingOperation.PlaceNewOrder:
                return this.placeNewOrderFromVisualTrading(newData.placeOrderParams, instrumentItem);
            case TerceraChartTradingOperation.CancelOrder:
                if (this.isMobileChart) {
                    return null;
                }
                return DataCache.FOrderExecutor.cancelOrdersPromise([order], confirm, PlacedFrom.WEB_CHART_VISUAL);
            case TerceraChartTradingOperation.CancelOrdersAccount:
                void DataCache.FOrderExecutor.cancelOrdersByAccountPromise(order.Account, confirm, PlacedFrom.WEB_CHART_VISUAL);
                return;
            case TerceraChartTradingOperation.CancelOrdersSymbol:
                void DataCache.FOrderExecutor.cancelOrdersByInstrumentAndAccountPromise(order.Instrument, order.Accounttrue, confirm, PlacedFrom.WEB_CHART_VISUAL);
                return;
            case TerceraChartTradingOperation.ClosePosition:
                if (this.isMobileChart) {
                    return null;
                }
                void DataCache.FOrderExecutor.closePositionsByIdPromise([order.PositionId], confirm, PlacedFrom.WEB_CHART_VISUAL_DB_CLICK);
                return;
            case TerceraChartTradingOperation.ModifyOrderPosition:
                // TODO?
                if (this.isMobileChart) {
                    return;
                }
                if (order.isClosingOrder() || order.isPosition) {
                    (_a = this.EditPositionScreen) === null || _a === void 0 ? void 0 : _a.show(order.PositionId, PlacedFrom.WEB_CHART_VISUAL, null, true);
                }
                else {
                    (_b = this.ModifyOrderScreen) === null || _b === void 0 ? void 0 : _b.show(order.OrderNumber, PlacedFrom.WEB_CHART_VISUAL);
                }
                return;
            case TerceraChartTradingOperation.ModifyOrderQuantity: {
                // TODO?
                if (this.isMobileChart) {
                    return;
                }
                const rect = newData.rect;
                const quantity = newData.quantity;
                this.myTradeNumeric
                    .set({
                    left: rect.X,
                    top: rect.Y,
                    width: rect.Width + 1,
                    height: rect.Height,
                    instrument: order.Instrument,
                    noArrowCB: true,
                    showArrows: false
                })
                    .then(function () {
                    this.myTradeNumeric.set({ value: quantity });
                }.bind(this));
                this.myTradeNumeric.set('visible', true);
                this.myTradeNumeric.Controls.QTYNumeric.setFocus();
                const orderToolView = newData.orderToolView;
                orderToolView.isQtyChanging = true;
                void new Promise(function (resolve, reject) {
                    this.chartOE.parent.myTradeNumericPromise = resolve;
                }.bind(this)).then(function (quantity) {
                    this.chartOE.parent.changeQuantity(order, {
                        quantity: new Quantity(quantity, BaseSettingsUtils.displayAmountInLots())
                    });
                    orderToolView.isQtyChanging = false;
                }.bind(this));
                return;
            }
            case TerceraChartTradingOperation.PlaceSL:
            case TerceraChartTradingOperation.ModifySL:
            case TerceraChartTradingOperation.PlaceTP:
            case TerceraChartTradingOperation.ModifyTP:
            case TerceraChartTradingOperation.ModifySLL:
                newData.placedFrom = PlacedFrom.WEB_CHART_VISUAL;
                if (order.isPosition) {
                    if (order.isPendingExerciseOptionStatus()) {
                        GenerateReportMessages.GenerateRefusalOnOptionExercise(order.GetInstrumentDisplayName()); // #122442
                    }
                    return this.changePositionSLTP(order, newData);
                }
                else {
                    return this.changeOrderSLTP(order, newData);
                }
            case TerceraChartTradingOperation.RemoveSL:
            case TerceraChartTradingOperation.RemoveTP:
                newData.placedFrom = PlacedFrom.WEB_CHART_VISUAL;
                if (order.isPosition) {
                    if (this.isMobileChart) {
                        return null;
                    }
                    const sltp = order[newData.action === TerceraChartTradingOperation.RemoveSL ? 'SLOrder' : 'TPOrder'];
                    void DataCache.FOrderExecutor.cancelOrdersPromise([sltp], confirm, PlacedFrom.WEB_CHART_VISUAL);
                }
                else {
                    return this.changeOrderSLTP(order, newData);
                }
                return;
            case TerceraChartTradingOperation.MoveOrder:
                newData.placedFrom = PlacedFrom.WEB_CHART_VISUAL;
                return this.moveOrder(order, newData);
            case TerceraChartTradingOperation.ChangeToMarket:
                void DataCache.FOrderExecutor.changeOrdersToMarketPromise([order.OrderNumber], confirm, PlacedFrom.WEB_CHART_VISUAL);
                return;
            case TerceraChartTradingOperation.AlertReplacement:
                DataCache.AlertManager.ReplaceAlertFromChart(order, newData);
                return;
            case TerceraChartTradingOperation.EditAlert: {
                // TODO?
                if (this.isMobileChart) {
                    return;
                }
                const panel = (_c = this.MainWindowManager) === null || _c === void 0 ? void 0 : _c.Factory.addPanel(PanelNames.CreateAlertPanel);
                panel.set('movable', true);
                panel.center();
                void panel.setUpdateParamsAsync(order);
                panel.setFocus();
                return;
            }
            case TerceraChartTradingOperation.RemoveAlert:
                DataCache.AlertManager.RemoveAlertProcess(order, null, confirm);
                return;
            case TerceraChartTradingOperation.ChartNewAlert:
                DataCache.AlertManager.ChartNewAlert(newData);
                return;
            case TerceraChartTradingOperation.ScrollToSelectedPrice: {
                const chart = this.terceraChartRactive.terceraChart;
                const mainWindow = chart.mainWindow;
                const FminY = chart.mainWindow.FminFloatY;
                const FmaxY = chart.mainWindow.FmaxFloatY;
                let price = newData.price;
                if (price < FminY)
                    price = mainWindow.PointsConverter.GetDataY(mainWindow.PointsConverter.GetScreenY(price) + 30);
                else
                    price = mainWindow.PointsConverter.GetDataY(mainWindow.PointsConverter.GetScreenY(price) - 30);
                const priceDelta = price <= FminY ? price - FminY : price - FmaxY;
                mainWindow.AutoScale = false;
                mainWindow.MoveY(FminY + priceDelta, FmaxY + priceDelta);
                return;
            }
        }
    }
    IsAllowedAction(action) {
        switch (action) {
            case TerceraChartTradingOperation.IsAllowOTHTrading:
                return this.isMobileChart ? false : this.myVisualWidget.get('isAllowOTHTrading');
        }
    }
    placeNewOrderFromVisualTrading(placeOrderParams, instrumentItem) {
        var _a, _b, _c;
        if (!this.myVisualWidget || ((_a = this.TradingNumericErrorChecker) === null || _a === void 0 ? void 0 : _a.HasErrors(this.myVisualWidget))) {
            return this.WM_placeNewOrderFromVisualTrading(placeOrderParams, instrumentItem);
        }
        placeOrderParams.fromChart = true;
        placeOrderParams.place = true;
        placeOrderParams.lTif = this.myVisualWidget.get('selectedItem').value;
        placeOrderParams.expireTime = this.myVisualWidget.get('gtdDate');
        placeOrderParams.quantity = this.myVisualWidget.get('quantity');
        placeOrderParams.placedFrom = PlacedFrom.WEB_CHART_VISUAL;
        const isVisibleOTHTrading = this.myVisualWidget.get('isVisibleOTHTrading');
        if (isVisibleOTHTrading) {
            placeOrderParams.isAllowOTHTrading = this.myVisualWidget.get('isAllowOTHTrading');
        }
        const chartOE = this.chartOE;
        placeOrderParams.productType = chartOE.get('productType');
        placeOrderParams.leverageValue = this.myVisualWidget.get('leverageValue');
        try {
            const ins = instrumentItem;
            const route = DataCache.Routes.getRouteByName(ins.Route);
            const isAllowedByTIFandOrderType = route.IsAllowableTif(placeOrderParams.lTif, placeOrderParams.lOrdType);
            if (!isAllowedByTIFandOrderType) {
                (_b = this.TerceraMessageBox) === null || _b === void 0 ? void 0 : _b.Show(Resources.getResource('screen.error.title'), Resources.getResource('IsAllowedResponceReason.NotAllowedRouteOrderType'), (_c = this.TerceraMessageBox) === null || _c === void 0 ? void 0 : _c.msgType.Error, null, null, false, true);
                return;
            }
        }
        catch (err) {
            console.error(err);
        }
        chartOE.OrderParamsSet(placeOrderParams, this.myVisualWidget);
    }
    WM_placeNewOrderFromVisualTrading(orderParams, instrumentItem) {
        orderParams.fromChart = true;
        orderParams.place = true;
        // placeOrderParams["lTif"] = this.myVisualWidget.get("selectedItem").value;
        // placeOrderParams["expireTime"] = this.myVisualWidget.get("gtdDate");
        // placeOrderParams["quantity"] = this.myVisualWidget.get("quantity");
        orderParams.placedFrom = PlacedFrom.WEB_CHART_VISUAL;
        // placeOrderParams["productType"] = chartOE.get('productType')
        // placeOrderParams["leverageValue"] = chartOE.get('leverageValue')
        if (isNullOrUndefined(orderParams)) {
            return null;
        }
        const orderType = orderParams.lOrdType;
        const orderTypeObj = DataCache.OrderParameterContainer.OrderTypes[orderType];
        const orderEditCtorData = {
            dataCache: DataCache,
            // TODO. UGLY. Details are in OrderEditBase.ts
            sltpMode: SLTPMode.Offset
        };
        // TODO. Ugly.
        const orderEdit = orderTypeObj.createOrderEditObject(orderEditCtorData);
        orderEdit.updateParameters(new OrderEditUpdateData(null, {
            account: orderParams.account,
            instrument: orderParams.instrument,
            side: orderParams.lBuySell,
            quantity: new Quantity(orderParams.quantity, true),
            tif: new TIF(orderParams.lTif, orderParams.expireTime),
            productType: orderParams.productType,
            leverageValue: orderParams.leverageValue,
            placedFrom: orderParams.placedFrom
        }));
        const chartSLTPHolder = this.getChartSLTPHolder(orderParams);
        // TODO. Ugly.
        switch (orderType) {
            case OrderType.Limit:
            case OrderType.TrailingStop:
                orderEdit.setBasePrice(orderParams.dPrice);
                break;
            case OrderType.Stop:
                orderEdit.setBasePrice(orderParams.dStopPrice);
                break;
            case OrderType.StopLimit:
                orderEdit.setBasePrice(orderParams.dPrice);
                orderEdit.setStopPrice(orderParams.dStopPrice);
                break;
            case OrderType.OCO:
                orderEdit.setBasePrice(orderParams.dPrice);
                orderEdit.setStopPrice(orderParams.dStopPrice);
                break;
        }
        // TODO. Ugly.
        orderEdit.setSLTP({ sltpHolder: chartSLTPHolder });
        return orderEdit;
    }
    moveOrder(order, newData) {
        const newPrice = newData.price;
        const ins = order.Instrument;
        const isLimitModify = newData.isLimitModify;
        const modifyOrdObj = this.createModifyOrderObject(order);
        if (modifyOrdObj === null) {
            if (newData.CancelCallback) {
                newData.CancelCallback();
                return;
            }
        }
        // Moving sl/tp along with order's price change.
        // TODO. UGLY as fook boi.
        // Trailing stop's sl/tp values are always in offset,
        // thus there's no need to change sl/tp.
        const ordTypes = OrderType;
        switch (order.OrderType) {
            case ordTypes.Stop:
            case ordTypes.StopLimit:
            case ordTypes.Limit: {
                // TODO. UGLY. It works just cuz JS doesn't give a dayum.
                const prevOrderPrice = modifyOrdObj.getBasePrice();
                let newOrderPrice = newPrice;
                if (order.OrderType === ordTypes.StopLimit) {
                    modifyOrdObj.setBasePrice(newPrice, isLimitModify, true);
                    newOrderPrice = modifyOrdObj.getBasePrice();
                }
                let priceDifferenceInTicks = SlTpUtils.getPriceDifferenceInTicks(prevOrderPrice, newOrderPrice, ins, BaseSettings.offsetType);
                if (priceDifferenceInTicks === 0) {
                    break;
                }
                // Ewww.
                priceDifferenceInTicks *= newOrderPrice > prevOrderPrice ? 1 : -1;
                const iSlTpParams = modifyOrdObj.getRawSLTP();
                const sltpHolder = iSlTpParams.sltpHolder;
                if (isValidNumber(sltpHolder.StopLossPriceValue)) {
                    if (sltpHolder.isSlAbsolute()) {
                        sltpHolder.StopLossPriceValue = ins.calculatePrice(sltpHolder.StopLossPriceValue, priceDifferenceInTicks);
                        if (isValidNumber(sltpHolder.StopLossLimitPriceValue)) {
                            sltpHolder.StopLossLimitPriceValue = ins.calculatePrice(sltpHolder.StopLossLimitPriceValue, priceDifferenceInTicks);
                        }
                    }
                }
                if (isValidNumber(sltpHolder.TakeProfitPriceValue) && sltpHolder.isTpAbsolute()) {
                    sltpHolder.TakeProfitPriceValue = ins.calculatePrice(sltpHolder.TakeProfitPriceValue, priceDifferenceInTicks);
                }
                // TODO. Ugly. Method is not base,
                // it works just cuz JS doesn't give a dayum.
                modifyOrdObj.setSLTP(iSlTpParams);
                break;
            }
        }
        modifyOrdObj.setBasePrice(newPrice, isLimitModify, true);
        modifyOrdObj.placedFrom = newData.placedFrom;
        if (this.isMobileChart) {
            return modifyOrdObj;
        }
        DataCache.FOrderExecutor.modifyOrderPromiseWithCallbacks(modifyOrdObj, null, null, this.isMobileChart)
            .then(function (confirmed) {
            if (newData.CancelCallback && !confirmed) {
                newData.CancelCallback();
            }
        })
            .catch(function () {
            const ex = new CustomErrorClass('MoveOrder error', 'ChartPanel.moveOrder', 'moveOrder -> modifyOrderPromiseWithCallbacks');
            ErrorInformationStorage.GetException(ex);
            if (newData.CancelCallback) {
                newData.CancelCallback();
            }
        })
            .finally(function () {
            // TODO. Refactor.
            modifyOrdObj.dispose();
        });
    }
    createModifyOrderObject(order) {
        const orderTypeObj = DataCache.OrderParameterContainer.GetOrderType(order.OrderType);
        return orderTypeObj.createModifyOrderObject({
            dataCache: DataCache,
            order
        });
    }
    // #region SL/TP modify logic
    changePositionSLTP(position, newData) {
        const action = newData.action;
        const modifyPosObj = new PositionEdit({ dataCache: DataCache, position });
        const newPrice = newData.price;
        const newIsTrStop = newData.ts;
        let sltpHolder;
        let isAllowOTHTradingSL = null;
        let isAllowOTHTradingTP = null;
        switch (action) {
            case TerceraChartTradingOperation.PlaceTP:
                isAllowOTHTradingTP = newData.isAllowOTHTrading;
                sltpHolder = this.addTP(position, newPrice);
                break;
            case TerceraChartTradingOperation.ModifyTP:
                sltpHolder = this.modifyTP(position, newPrice);
                break;
            case TerceraChartTradingOperation.PlaceSL:
                isAllowOTHTradingSL = newData.isAllowOTHTrading;
                sltpHolder = this.addSL(position, newPrice, newIsTrStop);
                break;
            case TerceraChartTradingOperation.ModifySL:
                sltpHolder = this.modifySL(position, newPrice, newIsTrStop);
                break;
            case TerceraChartTradingOperation.RemoveTP:
                sltpHolder = this.removeTP(position);
                break;
            case TerceraChartTradingOperation.RemoveSL:
                sltpHolder = this.removeSL(position);
                break;
            case TerceraChartTradingOperation.ModifySLL:
                sltpHolder = this.modifySLL(position, newPrice);
                break;
            default:
                sltpHolder = position.createSlTpHolder();
                break;
        }
        if (sltpHolder.TakeProfitPriceValue || sltpHolder.StopLossPriceValue) {
            sltpHolder.SLTPTriggerShortValue = SLTPTriggerUtils.GetShortByOrder(position);
        }
        modifyPosObj.placedFrom = newData.placedFrom;
        modifyPosObj.setSLTP({ sltpHolder, isAllowOTHTradingSL, isAllowOTHTradingTP });
        // Trailing stop implicit modification fix.
        // For instance: dragging of tp order causes tr stop order change as well
        // due to setting up trailing stop value in offset ticks.
        // Server treats it as an offset relative to current price...
        // Closing trailing stop order receives price updates from a server,
        // but Order.TrStopOffset field isn't recalculated accordingly.
        // Whatever. Refactor this pile of shit.
        modifyPosObj.setCanEditSLOrTrailingStop(action === TerceraChartTradingOperation.PlaceSL ||
            action === TerceraChartTradingOperation.ModifySL ||
            action === TerceraChartTradingOperation.RemoveSL ||
            action === TerceraChartTradingOperation.ModifySLL);
        modifyPosObj.setCanEditTP(action === TerceraChartTradingOperation.PlaceTP ||
            action === TerceraChartTradingOperation.ModifyTP ||
            action === TerceraChartTradingOperation.RemoveTP);
        if (this.isMobileChart) {
            return modifyPosObj;
        }
        DataCache.FOrderExecutor.modifyPositionPromise(modifyPosObj, this.isMobileChart)
            .then(function (confirmed) {
            if (newData.CancelCallback && !confirmed) {
                newData.CancelCallback();
            }
        })
            .catch(function () {
            if (newData.CancelCallback) {
                newData.CancelCallback();
            }
        })
            .finally(function () {
            // TODO. Refactor.
            modifyPosObj.dispose();
        });
    }
    changeOrderSLTP(order, newData) {
        const newPrice = newData.price;
        const newIsTrStop = newData.ts;
        const modifyOrdObj = this.createModifyOrderObject(order);
        const action = newData.action;
        let sltpHolder;
        switch (action) {
            case TerceraChartTradingOperation.PlaceTP:
                sltpHolder = this.addTP(order, newPrice);
                break;
            case TerceraChartTradingOperation.ModifyTP:
                sltpHolder = this.modifyTP(order, newPrice);
                break;
            case TerceraChartTradingOperation.PlaceSL:
                sltpHolder = this.addSL(order, newPrice, newIsTrStop);
                break;
            case TerceraChartTradingOperation.ModifySL:
                sltpHolder = this.modifySL(order, newPrice, newIsTrStop);
                break;
            case TerceraChartTradingOperation.ModifySLL:
                sltpHolder = this.modifySLL(order, newPrice);
                break;
            case TerceraChartTradingOperation.RemoveTP:
                sltpHolder = this.removeTP(order);
                break;
            case TerceraChartTradingOperation.RemoveSL:
                sltpHolder = this.removeSL(order);
                break;
            default:
                sltpHolder = order.createSlTpHolder();
                break;
        }
        if (sltpHolder.TakeProfitPriceValue || sltpHolder.StopLossPriceValue) {
            sltpHolder.SLTPTriggerShortValue = SLTPTriggerUtils.GetShortByOrder(order);
        }
        modifyOrdObj.placedFrom = newData.placedFrom;
        modifyOrdObj.setSLTP({ sltpHolder });
        if (this.isMobileChart) {
            return modifyOrdObj;
        }
        DataCache.FOrderExecutor.modifyOrderPromiseWithCallbacks(modifyOrdObj, null, null, this.isMobileChart)
            .then(function (confirmed) {
            if (newData.CancelCallback && !confirmed) {
                newData.CancelCallback();
            }
        })
            .catch(function () {
            const ex = new CustomErrorClass('ChangeOrderSLTP error', 'ChartPanel.changeOrderSLTP', 'changeOrderSLTP -> modifyOrderPromiseWithCallbacks');
            ErrorInformationStorage.GetException(ex);
            if (newData.CancelCallback) {
                newData.CancelCallback();
            }
        })
            .finally(function () {
            // TODO. Refactor.
            modifyOrdObj.dispose();
        });
    }
    addSL(tradingObject, slPrice, isTrStop) {
        const isPosition = tradingObject instanceof Position;
        const sltpHolder = tradingObject.createSlTpHolder();
        const inOffset = sltpHolder.isTpEmpty() ? BaseSettings.isSlTpInOffset : !sltpHolder.isTpAbsolute();
        if (isTrStop) {
            const basePrice = tradingObject.GetBasePriceForTrailingStop();
            sltpHolder.StopLossPriceType = SlTpPriceType.TrOffset;
            sltpHolder.StopLossPriceValue = SlTpUtils.calculateTicks(basePrice, slPrice, tradingObject.Instrument, BaseSettings.offsetType);
        }
        else if (!isPosition && inOffset) {
            const price = tradingObject.Price;
            let slPriceType;
            if (BaseSettings.isUseStopLimitInsteadStop()) {
                slPriceType = SlTpPriceType.Offset;
            }
            else if (!sltpHolder.isTpEmpty()) {
                slPriceType = sltpHolder.TakeProfitPriceType;
            }
            else {
                slPriceType = SlTpUtils.getSLTPPriceType(BaseSettings.offsetType);
            }
            const slPriceValue = slPriceType === SlTpPriceType.Percent
                ? SlTpUtils.calculatePercent(price, slPrice)
                : SlTpUtils.calculateTicks(price, slPrice, tradingObject.Instrument, BaseSettings.offsetType);
            sltpHolder.StopLossPriceType = slPriceType;
            sltpHolder.StopLossPriceValue = slPriceValue;
            if (BaseSettings.isUseStopLimitInsteadStop()) {
                sltpHolder.StopLossLimitPriceValue = BaseSettings.limitOffsetTicks;
            }
        }
        else {
            sltpHolder.StopLossPriceType = SlTpPriceType.Absolute;
            sltpHolder.StopLossPriceValue = slPrice;
            if (BaseSettings.isUseStopLimitInsteadStop()) {
                const slSign = tradingObject.BuySell === OperationType.Buy ? -1 : 1;
                sltpHolder.StopLossLimitPriceValue = tradingObject.Instrument.calculatePrice(slPrice, BaseSettings.limitOffsetTicks * slSign);
            }
        }
        return sltpHolder;
    }
    modifySL(tradingObject, slPrice, isTrStop) {
        const sltpHolder = tradingObject.createSlTpHolder();
        if (isTrStop) {
            const basePrice = tradingObject.GetBasePriceForTrailingStop();
            sltpHolder.StopLossPriceType = SlTpPriceType.TrOffset;
            sltpHolder.StopLossPriceValue = SlTpUtils.calculateTicks(basePrice, slPrice, tradingObject.Instrument, BaseSettings.offsetType);
            sltpHolder.StopLossLimitPriceValue = null;
        }
        else if (sltpHolder.isSlAbsolute()) {
            const absoluteOffset = slPrice - sltpHolder.StopLossPriceValue;
            sltpHolder.StopLossPriceValue = slPrice;
            if (!sltpHolder.isSllEmpty()) {
                sltpHolder.StopLossLimitPriceValue = sltpHolder.StopLossLimitPriceValue + absoluteOffset;
            }
        }
        else if (sltpHolder.StopLossPriceType === SlTpPriceType.TrOffset) {
            sltpHolder.StopLossPriceType = SlTpPriceType.Absolute;
            sltpHolder.StopLossPriceValue = slPrice;
        }
        else {
            // Only in order case
            const newOffset = sltpHolder.StopLossPriceType === SlTpPriceType.Percent
                ? SlTpUtils.calculatePercent(tradingObject.Price, slPrice)
                : SlTpUtils.calculateTicks(tradingObject.Price, slPrice, tradingObject.Instrument, BaseSettings.offsetType);
            sltpHolder.StopLossPriceValue = newOffset;
        }
        return sltpHolder;
    }
    modifySLL(tradingObject, sllPrice) {
        const sltpHolder = tradingObject.createSlTpHolder();
        if (sltpHolder.isSlAbsolute()) {
            sltpHolder.StopLossLimitPriceValue = sllPrice;
        }
        else {
            // Only in order case
            const slPrice = tradingObject.GetStopLossInPriceValue();
            const sign = (tradingObject.BuySell === OperationType.Buy ? 1 : -1) * Math.sign(slPrice - sllPrice);
            sltpHolder.StopLossLimitPriceValue =
                SlTpUtils.calculateTicks(tradingObject.GetStopLossInPriceValue(), sllPrice, tradingObject.Instrument, BaseSettings.offsetType) *
                    sign;
        }
        return sltpHolder;
    }
    removeSL(tradingObject) {
        const sltpHolder = tradingObject.createSlTpHolder();
        sltpHolder.StopLossPriceValue = NaN;
        sltpHolder.StopLossLimitPriceValue = null;
        return sltpHolder;
    }
    addTP(tradingObject, tpPrice) {
        const isPosition = tradingObject instanceof Position;
        const sltpHolder = tradingObject.createSlTpHolder();
        const inOffset = sltpHolder.isSlEmpty() || sltpHolder.isTrailingStop()
            ? BaseSettings.isSlTpInOffset
            : !sltpHolder.isSlAbsolute();
        if (!isPosition && inOffset) {
            const price = tradingObject.Price;
            let tpPriceType;
            if (sltpHolder.isSlEmpty() || sltpHolder.isTrailingStop()) {
                tpPriceType = SlTpUtils.getSLTPPriceType(BaseSettings.offsetType);
            }
            else {
                tpPriceType = sltpHolder.StopLossPriceType;
            }
            const tpPriceValue = tpPriceType === SlTpPriceType.Percent
                ? SlTpUtils.calculatePercent(price, tpPrice)
                : SlTpUtils.calculateTicks(price, tpPrice, tradingObject.Instrument, BaseSettings.offsetType);
            sltpHolder.TakeProfitPriceType = tpPriceType;
            sltpHolder.TakeProfitPriceValue = tpPriceValue;
        }
        else {
            sltpHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
            sltpHolder.TakeProfitPriceValue = tpPrice;
        }
        return sltpHolder;
    }
    modifyTP(tradingObject, tpPrice) {
        const sltpHolder = tradingObject.createSlTpHolder();
        if (sltpHolder.isTpAbsolute()) {
            sltpHolder.TakeProfitPriceValue = tpPrice;
        }
        else {
            // Only in order case
            const orderPrice = tradingObject.Price;
            sltpHolder.TakeProfitPriceValue =
                sltpHolder.TakeProfitPriceType === SlTpPriceType.Percent
                    ? SlTpUtils.calculatePercent(orderPrice, tpPrice)
                    : SlTpUtils.calculateTicks(orderPrice, tpPrice, tradingObject.Instrument, BaseSettings.offsetType);
        }
        return sltpHolder;
    }
    removeTP(tradingObject) {
        const sltpHolder = tradingObject.createSlTpHolder();
        sltpHolder.TakeProfitPriceValue = NaN;
        return sltpHolder;
    }
}
